import React from 'react'
import styled from 'styled-components'
import { PageContent, PageHeader } from '../../../common'
import { Colors, ChainId, INTEGRAL_TOKEN, TIME_RELEASE_ADDRESS } from '../../../constants'
import { useClaim, useMerkleClaim } from './useClaim'
import { ClaimButton } from './ClaimButton'
import { TimeRelease, useApiMerkleTimeRelease, useEthers } from '../../../hooks'
import { ClaimPositions } from './ClaimPositions'
import { CurrencyValue } from '../../../model'
import { BigNumber } from 'ethers'
import { BN_ZERO, getDisplayValue } from '../../../utils'

const COLUMNS_NUMBER = 4

interface SingleClaimProps {
  index: number
  timeRelease: TimeRelease
}

export function SingleClaim() {
  const { chainId } = useEthers()
  const { option1Total, option2Total, option1Claimed, option2Claimed, option1Claimable, option2Claimable } = useClaim()

  const claimPositions = [
    {
      total: getDisplayValue(getCurrencyValue(chainId, option1Total), 6),
      claimed: getDisplayValue(getCurrencyValue(chainId, option1Claimed), 6),
      claimable: getDisplayValue(getCurrencyValue(chainId, option1Claimable), 6),
    },
    {
      total: getDisplayValue(getCurrencyValue(chainId, option2Total), 6),
      claimed: getDisplayValue(getCurrencyValue(chainId, option2Claimed), 6),
      claimable: getDisplayValue(getCurrencyValue(chainId, option2Claimable), 6),
    },
  ]

  return (
    <>
      <Row>
        <td>
          <Label>Release #0</Label>
        </td>
        <td>
          <ClaimButton
            option1Claimable={getCurrencyValue(chainId, option1Claimable)}
            option2Claimable={getCurrencyValue(chainId, option2Claimable)}
            timeReleaseAddress={chainId ? TIME_RELEASE_ADDRESS[chainId] : ''}
          />
        </td>
      </Row>
      <DarkRow>
        <td colSpan={COLUMNS_NUMBER}>
          <ClaimPositions positions={claimPositions} />
        </td>
      </DarkRow>
    </>
  )
}
export function SingleMerkleReleaseClaim({ index, timeRelease }: SingleClaimProps) {
  const { chainId } = useEthers()
  const { option1Claimed, option2Claimed } = useMerkleClaim(timeRelease.address)
  const [allocation1, allocation2] = [
    BigNumber.from(timeRelease.claim?.amount1 || 0),
    BigNumber.from(timeRelease.claim?.amount2 || 0),
  ]

  let [option1Claimable, option2Claimable] = [
    option1Claimed ? BigNumber.from(timeRelease.claim?.claimable1 || 0).sub(option1Claimed) : undefined,
    option2Claimed ? BigNumber.from(timeRelease.claim?.claimable2 || 0).sub(option2Claimed) : undefined,
  ]

  if (option1Claimable?.lt(BN_ZERO)) {
    option1Claimable = BN_ZERO
  }

  if (option2Claimable?.lt(BN_ZERO)) {
    option2Claimable = BN_ZERO
  }

  const claimPositions = [
    {
      total: getDisplayValue(getCurrencyValue(chainId, allocation1), 6),
      claimed: getDisplayValue(getCurrencyValue(chainId, option1Claimed), 6),
      claimable: getDisplayValue(getCurrencyValue(chainId, option1Claimable), 6),
    },
    {
      total: getDisplayValue(getCurrencyValue(chainId, allocation2), 6),
      claimed: getDisplayValue(getCurrencyValue(chainId, option2Claimed), 6),
      claimable: getDisplayValue(getCurrencyValue(chainId, option2Claimable), 6),
    },
  ]
  return (
    <>
      <Row>
        <td>
          <Label>
            Release #{index} ({new Date(timeRelease.startDate).toLocaleDateString()})
          </Label>
        </td>
        <td>
          <ClaimButton
            option1Claimable={getCurrencyValue(chainId, option1Claimable)}
            option2Claimable={getCurrencyValue(chainId, option2Claimable)}
            timeReleaseAddress={timeRelease.address}
            timeReleaseClaim={timeRelease.claim}
          />
        </td>
      </Row>
      <DarkRow>
        <td colSpan={COLUMNS_NUMBER}>
          <ClaimPositions positions={claimPositions} />
        </td>
      </DarkRow>
    </>
  )
}
export function Claim() {
  const { timeReleases } = useApiMerkleTimeRelease()
  return (
    <PageContentStyled>
      <PageHeader label="Claim ITGR" />
      <Table>
        <thead>
          <Row>
            <th scope="col">Release</th>
            <th scope="col">Actions</th>
          </Row>
        </thead>
        <tbody>
          <SingleClaim />
          {timeReleases.map((timeRelease) => (
            <SingleMerkleReleaseClaim key={timeRelease.address} index={timeRelease.id} timeRelease={timeRelease} />
          ))}
        </tbody>
      </Table>
    </PageContentStyled>
  )
}

function getCurrencyValue(chainId?: ChainId, value?: BigNumber) {
  return chainId && value && new CurrencyValue(INTEGRAL_TOKEN[chainId], value)
}

const PageContentStyled = styled(PageContent)`
  margin: 0 15px 0 auto;
  background: rgb(38, 41, 44);
  background: linear-gradient(0deg, rgba(38, 41, 44, 1) 0%, rgba(52, 57, 68, 1) 100%);
  border: 1px solid ${Colors.DarkGray};

  @media (max-width: 1024px) {
    margin: 0 15px;
  }
`

const Table = styled.table`
  border-spacing: 0;
  overflow-x: auto;

  th,
  td {
    margin: 0;
    padding: 15px 15px;
    font-size: 12px;
    color: ${Colors.White};

    &:first-child {
      text-align: left;
    }
  }

  th {
    width: 100%;
    background-color: #272b32;
    border: 1px solid #272b32;
    text-transform: uppercase;
  }

  td {
    text-align: center;
  }
`

const Row = styled.tr`
  &:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.1);
  }
`

const DarkRow = styled.tr`
  background-color: rgba(0, 0, 0, 0.1);
`

const Label = styled.span`
  white-space: nowrap;
`
